import { useMemo } from 'react'
import { matchPath } from 'react-router'
import { useLocation } from 'react-router-dom'
import { AppLocation, SidenavContentType } from 'src/navigation/appLocations'
import {
  AppRoute,
  authedBackgroundAppRoute,
  authedLeftFlyoutAppRoute,
  authedPopupAppRoute,
  authedRightFlyoutAppRoute,
  unauthedAppRoute,
} from 'src/navigation/appRoutes'
import ReimbursementsAppRoute from 'src/navigation/appRoutes/expenses/reimbursements.appRoute'
import APIKeysAppRoute from 'src/navigation/appRoutes/organization/APIKeysAppRoute'
import AutomationSettingsAppRoute from 'src/navigation/appRoutes/organization/automationSettingsAppRoute'
import BusinessRulesAppRoute from 'src/navigation/appRoutes/organization/businessRules.appRoute'
import CardFeedCreateAppRoute from 'src/navigation/appRoutes/organization/cardFeedCreate.appRoute'
import CardFeedDetailsAppRoute from 'src/navigation/appRoutes/organization/cardFeedDetails.appRoute'
import CardFeedsAppRoute from 'src/navigation/appRoutes/organization/cardFeeds.appRoute'
import CustomFieldDetailAppRoute from 'src/navigation/appRoutes/organization/customFieldDetail.appRoute'
import DepartmentsAppRoute from 'src/navigation/appRoutes/organization/departments.appRoute'
import ExpenseCategoriesAppRoute from 'src/navigation/appRoutes/organization/expenseCategories.appRoute'
import ExpenseCategoriesAdditionalFieldsAppRoute from 'src/navigation/appRoutes/organization/expenseCategoriesAdditionalFields.appRoute'
import ExpenseRequirementsAppRoute from 'src/navigation/appRoutes/organization/expenseRequirements.appRoute'
import ManageSubscriptionAppRoute from 'src/navigation/appRoutes/organization/manageSubscription.appRoute'
import OrganizationInfoAppRoute from 'src/navigation/appRoutes/organization/organizationInfo.appRoute'
import OrganizationIntegrationsAppRoute from 'src/navigation/appRoutes/organization/organizationIntegrations.appRoute'
import OrgSettingsReimbursementsAppRoute from 'src/navigation/appRoutes/organization/reimbursements.appRoute'
import SSOSettingsAppRoute from 'src/navigation/appRoutes/organization/ssoSettings.appRoute'

type FlatRoute = {
  id?: AppLocation
  paths: string[]
  exact?: boolean
  contentType: SidenavContentType
}

export interface ExtendLocationResult {
  location: AppLocation
  params: Record<string, string>
  contentType: SidenavContentType
  matchedPath?: string
}

type MaybeThunk<T> = T | (() => T)

const toPaths = (p?: string | readonly string[]) =>
  !p ? [] : Array.isArray(p) ? [...p] : [p]

const asArray = <T,>(v?: MaybeThunk<T[]>): T[] =>
  !v ? [] : typeof v === 'function' ? (v as () => T[])() : v

const DEFAULT_ROOTS: AppRoute[] = [
  authedBackgroundAppRoute,
  authedPopupAppRoute,
  authedRightFlyoutAppRoute,
  authedLeftFlyoutAppRoute,
  unauthedAppRoute,
]

function sortBySpecificity(a: string, b: string) {
  const segs = (p: string) => p.split('/').filter(Boolean)
  const isParam = (s: string) => s.startsWith(':')
  const A = segs(a),
    B = segs(b)

  // 1) More segments = more specific
  if (A.length !== B.length) return B.length - A.length

  // 2) Fewer params = more specific (e.g., /a/b vs /a/:id)
  const aParams = A.filter(isParam).length
  const bParams = B.filter(isParam).length
  if (aParams !== bParams) return aParams - bParams

  // 3) Longer string as final tiebreaker
  return b.length - a.length
}

function collectAllRoutes(root: AppRoute): AppRoute[] {
  const out: AppRoute[] = []
  const visit = (node?: AppRoute, parent?: string) => {
    if (!node) return
    const paths = toPaths(node.routeProps?.path)
    if (paths.length) out.push(node)

    const kids = asArray(node.subRoutes)
    for (const k of kids) visit(k)
  }
  visit(root)
  return out
}

export function flattenRoutes(root: AppRoute): FlatRoute[] {
  const nodes = collectAllRoutes(root)
  const flats = nodes.map((n) => ({
    id: n.id,
    paths: toPaths(n.routeProps?.path),
    exact: n.routeProps?.exact,
    contentType: n.meta?.contentType ?? SidenavContentType.Main,
  }))

  const seen = new Set<string>()
  return flats
    .map((fr) => ({
      ...fr,
      paths: fr.paths.filter((p) =>
        seen.has(p) ? false : (seen.add(p), true),
      ),
    }))
    .filter((fr) => fr.paths.length > 0)
}

// ---- NEW: flatten all roots (and drop '/')
function flattenAllRoots(roots: AppRoute[]): FlatRoute[] {
  const merged: FlatRoute[] = []
  for (const r of roots) merged.push(...flattenRoutes(r))
  return merged.map((fr) => ({
    ...fr,
    paths: fr.paths.filter((p) => p !== '/'),
  }))
}

export function useExtendLocation(
  roots: AppRoute[] = DEFAULT_ROOTS,
): ExtendLocationResult {
  const rrLoc = useLocation()

  const updatedRoots = [
    ...roots,
    CustomFieldDetailAppRoute,
    ExpenseCategoriesAdditionalFieldsAppRoute,
    ExpenseCategoriesAppRoute,
    DepartmentsAppRoute,
    CardFeedsAppRoute,
    CardFeedCreateAppRoute,
    CardFeedDetailsAppRoute,
    OrganizationIntegrationsAppRoute,
    BusinessRulesAppRoute,
    OrgSettingsReimbursementsAppRoute,
    ReimbursementsAppRoute,
    OrganizationInfoAppRoute,
    SSOSettingsAppRoute,
    ManageSubscriptionAppRoute,
    APIKeysAppRoute,
    AutomationSettingsAppRoute,
    ExpenseRequirementsAppRoute,
  ]

  const flat = useMemo(() => flattenAllRoots(updatedRoots), [updatedRoots])

  // sort all paths globally before matching
  const sorted = [...flat]
    .flatMap((fr) => fr.paths.map((p) => ({ ...fr, path: p })))
    .sort((a, b) => sortBySpecificity(a.path, b.path))

  for (const fr of sorted) {
    for (const p of fr.paths) {
      const match = matchPath(rrLoc.pathname, {
        path: p,
        exact: true,
        strict: false,
      })
      if (match) {
        const params = (match.params ?? {}) as Record<string, string>
        const loc = fr.id // avoid shadowing rrLoc
        return {
          location: loc || AppLocation.Home,
          params,
          contentType: fr.contentType,
          matchedPath: p,
        }
      }
    }
  }

  return {
    location: AppLocation.Home,
    params: {},
    contentType: SidenavContentType.Main,
  }
}
